import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { setCurrentRestaurant, setCurrentFranchise } from "@/redux/features/user/user.slice";
import { useActivateRestaurantMutation, useActivateFranchiseMutation } from "@/redux/services/restaurant/restaurant.api";
import toast from "react-hot-toast";
import { Info } from "lucide-react";
import { useState } from "react";
import { Roles } from "@/redux/services/user/user.type";
import { logger } from "@/services/Logger";

const Banner = () => {
  const [activateRestaurant, { isLoading: isActivatingRestaurant }] = useActivateRestaurantMutation();
  const [activateFranchise, { isLoading: isActivatingFranchise }] = useActivateFranchiseMutation();
  const { currentRestaurant, currentUser, currentFranchise } = useAppSelector(
    (state) => state.user
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const isLoading = isActivatingRestaurant || isActivatingFranchise;
  const dispatch = useAppDispatch();
  const [isConfirming, setIsConfirming] = useState(false);

  // The banner and its « Activer » button are for demo accounts only: the
  // activate endpoints accept only a demo restaurant/franchise, so a disabled
  // one must not see it (SFRESTO-140).
  const status = isOwner ? currentFranchise?.status : currentRestaurant?.status;
  if (status !== "demo") {
    return null;
  }

  const handleActivate = async () => {
    if (!isConfirming) {
      setIsConfirming(true);
      return;
    }

    try {
      if (isOwner) {
        const res = await activateFranchise({ id: currentFranchise?.id });
        if (res.data) {
          dispatch(setCurrentFranchise(res.data));
          toast.success("Franchise activée avec succès");
        } else {
          throw new Error("Activation failed");
        }
      } else {
        const res = await activateRestaurant({ id: currentRestaurant?.id });
        if (res.data) {
          dispatch(setCurrentRestaurant(res.data));
          toast.success("Restaurant activé avec succès");
        } else {
          throw new Error("Activation failed");
        }
      }
    } catch (error) {
      logger.error("Banner", "Activation error:", error);
      toast.error(
        isOwner
          ? "Une erreur est survenue lors de l'activation de la franchise"
          : "Une erreur est survenue lors de l'activation du restaurant"
      );
    } finally {
      setIsConfirming(false);
    }
  };

  const renderLoadingSpinner = () => (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  const getButtonLabel = () => {
    if (isLoading) {
      return (
        <span className="flex items-center">
          {renderLoadingSpinner()}
          Activation en cours...
        </span>
      );
    } else if (isConfirming) {
      return "Confirmer l'activation?";
    } else {
      return isOwner ? "Activer votre franchise" : "Activer votre restaurant";
    }
  };

  const getButtonClassName = () => {
    const baseClasses =
      "flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent";

    if (isConfirming) {
      return `${baseClasses} bg-red-500 hover:bg-red-600 text-white`;
    } else {
      return `${baseClasses} bg-accent hover:bg-accent/90 text-white`;
    }
  };

  const renderActivationButton = () => {
    if (!currentUser?.roles?.includes(Roles.manager) && !currentUser?.roles?.includes(Roles.owner)) {
      return null;
    }

    return (
      <button
        className={getButtonClassName()}
        onClick={handleActivate}
        disabled={isLoading}
      >
        {getButtonLabel()}
      </button>
    );
  };

  const renderBannerMessage = () => (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 p-1.5 rounded-full bg-accent/15">
        <Info className="h-4 w-4 text-accent" />
      </div>
      <p className="text-sm text-accent font-medium">
        {isOwner
          ? "Votre franchise est en mode demo. Activez-la pour profiter de toutes les fonctionnalités."
          : "Votre restaurant est en mode demo. Toutes les commandes et notifications seront supprimées après activation."}
      </p>
    </div>
  );

  return (
    <div className="bg-accent-light border-b border-accent/10">
      <div className="px-4 py-2.5 md:px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
          {renderBannerMessage()}
          {renderActivationButton()}
        </div>
      </div>
    </div>
  );
};

export default Banner;
